import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const empty = {
  name: "", client: "", location: "", service_type: "Contracting",
  status: "in_progress", progress: 0, budget_sar: 0,
  start_date: "", end_date: "",
};

export default function ProjectsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/projects"); setItems(data || []); }
    catch { toast.error("Failed to load projects"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/projects", { ...form, progress: Number(form.progress), budget_sar: Number(form.budget_sar) });
      toast.success("Project added");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/projects/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const statusBadge = (s) => {
    if (s === "completed") return "bg-primary/15 text-primary border-primary/30";
    if (s === "in_progress") return "bg-secondary/10 text-secondary border-secondary/30";
    return "bg-muted text-muted-foreground border-border";
  };

  return (
    <div className="space-y-8" data-testid="projects-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="overline">/ Project Portfolio</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">Active & delivered work</h1>
          <p className="text-muted-foreground mt-2">Manage contracts, progress and SLAs across the Kingdom.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm h-11 bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground" data-testid="project-add-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="project-add-dialog">
            <DialogHeader><DialogTitle>Add project</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "name", l: "Name", c: 2 },
                { k: "client", l: "Client" },
                { k: "location", l: "Location" },
                { k: "service_type", l: "Service type" },
                { k: "status", l: "Status (planned, in_progress, completed)" },
                { k: "progress", l: "Progress %", t: "number" },
                { k: "budget_sar", l: "Budget (SAR)", t: "number" },
                { k: "start_date", l: "Start (YYYY-MM-DD)" },
                { k: "end_date", l: "End (YYYY-MM-DD)" },
              ].map((f) => (
                <div key={f.k} className={f.c === 2 ? "col-span-2" : ""}>
                  <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">{f.l}</Label>
                  <Input
                    type={f.t || "text"}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="rounded-sm"
                    data-testid={`project-input-${f.k}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={create} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="project-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="projects-grid">
        {items.length === 0 && (
          <Card className="rounded-sm p-10 text-center text-muted-foreground col-span-full">
            No projects yet.
          </Card>
        )}
        {items.map((p) => (
          <Card key={p.id} className="rounded-sm p-6 hover:border-primary transition-colors" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <Badge variant="outline" className={`rounded-sm capitalize ${statusBadge(p.status)}`}>
                {String(p.status).replace("_", " ")}
              </Badge>
              <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`project-delete-${p.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <h3 className="mt-4 text-lg font-bold leading-snug">{p.name}</h3>
            <div className="mt-1 text-sm text-muted-foreground">{p.client} · {p.location}</div>
            <div className="mt-5">
              <div className="flex justify-between text-xs mb-2">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-semibold tabular-nums">{p.progress}%</span>
              </div>
              <Progress value={p.progress} className="h-1.5 rounded-none" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-muted-foreground uppercase tracking-[0.18em]">Service</div>
                <div className="font-semibold mt-1">{p.service_type}</div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase tracking-[0.18em]">Budget</div>
                <div className="font-semibold mt-1 tabular-nums">SAR {Number(p.budget_sar).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase tracking-[0.18em]">Start</div>
                <div className="font-semibold mt-1">{p.start_date}</div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase tracking-[0.18em]">End</div>
                <div className="font-semibold mt-1">{p.end_date}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
